import { isNil } from 'lodash';
import React from 'react';
import { View } from '@shared/components/View';
import { GeolocationSnapshot } from '@shared/components/blocks/types';
import { MBLoadingIndicator } from '@shared/components/loading';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { grid } from '@shared/styles';
import { useListUsers } from '@shared/util/hooks';
import { InputUserAndDate } from '../inputUserAndDate';
import { GeolocationMapPreview } from './mapPreview';

interface Props {
  geolocation: GeolocationSnapshot;
  collapsed?: boolean;
  onClear?(): void;
  showUserName?: boolean;
  indentMetaData?: boolean;
}

export function GeolocationContent({
  geolocation,
  collapsed,
  onClear,
  showUserName,
  indentMetaData,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const { users, loading } = useListUsers({
    input: {
      ids: [geolocation?.userId ?? -1],
      excludeSystemUsers: true,
      orderBys: [{ orderBy: 'firstName', direction: 'ASC' }],
    },
    skip: !showUserName || isNil(geolocation.userId),
  });
  const user = users[0];
  const { date } = geolocation;

  if (loading) {
    return (
      <View row center={!collapsed} style={{ height: collapsed ? grid(3) : grid(12) }}>
        <MBLoadingIndicator />
      </View>
    );
  }

  if (collapsed) {
    return (
      <View row centerVertically style={{ width: '100%' }}>
        <View marginRight={1.25}>
          <GeolocationMapPreview
            width={grid(5)}
            height={grid(5)}
            lat={geolocation.coords.lat}
            lon={geolocation.coords.lon}
            onClear={onClear}
          />
        </View>
        <InputUserAndDate user={user} date={date} collapsed />
      </View>
    );
  }

  if (responsiveForms) {
    return (
      <View row>
        <GeolocationMapPreview
          width={grid(12)}
          height={grid(12)}
          lat={geolocation.coords.lat}
          lon={geolocation.coords.lon}
          iconUrl="https://assets.coastapp.com/address-pin.png"
        />
        <View paddingHorizontal={2}>
          <InputUserAndDate user={user} date={date} indent={false} />
        </View>
      </View>
    );
  }

  return (
    <View style={{ width: '100%' }} paddingTop={0.5}>
      <GeolocationMapPreview
        lat={geolocation.coords.lat}
        lon={geolocation.coords.lon}
        onClear={onClear}
      />
      <InputUserAndDate user={user} date={date} indent={indentMetaData} />
    </View>
  );
}
